import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircleIcon, ExclamationCircleIcon } from '@nimbus-ds/icons'
import { Button, Card, Spinner, Text, Title } from '@nimbus-ds/components'

type AuthStatus = 'loading' | 'success' | 'error'

export function AuthSuccessPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [error, setError] = useState<string | null>(null)
  const [userInfo, setUserInfo] = useState<any>(null)

  useEffect(() => {
    const handleAuthSuccess = async () => {
      try {
        // Check for auth errors first
        const authError = searchParams.get('auth_error')
        const message = searchParams.get('message')

        if (authError) {
          throw new Error(message || `Authentication error: ${authError}`)
        }

        // Get JWT tokens from URL
        const accessToken = searchParams.get('access_token')
        const refreshToken = searchParams.get('refresh_token')

        if (!accessToken) {
          throw new Error('No access token received')
        }

        // Decode and store JWT tokens
        const jwtToken = decodeURIComponent(accessToken)
        const refreshJWT = refreshToken ? decodeURIComponent(refreshToken) : null

        console.log('Processing user authentication success:', {
          tokenPreview: jwtToken.substring(0, 20) + '...',
          hasRefreshToken: !!refreshJWT
        })

        // Store JWT tokens in localStorage
        localStorage.setItem('tnx_access_token', jwtToken)
        if (refreshJWT) {
          localStorage.setItem('tnx_refresh_token', refreshJWT)
        }

        // Decode JWT to get user info (basic decode for display)
        try {
          const payload = JSON.parse(atob(jwtToken.split('.')[1]))

          // Extract store information from the JWT stores object
          const stores = payload.stores || {}
          const storeIds = Object.keys(stores)
          const primaryStoreId = storeIds[0] // Use first store as primary

          // Extract store name from user_data if available
          const storeName = payload.user_data?.name?.es || payload.user_data?.name || 'Tu tienda'

          const userInfo = {
            user_id: payload.sub,
            email: payload.email,
            store_id: primaryStoreId, // For experiments API compatibility
            primary_store_id: primaryStoreId,
            store_permissions: stores,
            stores: stores, // Keep both formats for compatibility
            store_name: storeName // Add store name for display
          }

          localStorage.setItem('tnx_user_info', JSON.stringify(userInfo))
          setUserInfo(userInfo)
        } catch (decodeError) {
          console.warn('Could not decode JWT for display:', decodeError)
          setUserInfo({ user_id: 'User', email: 'user@example.com' })
        }

        setStatus('success')

        // Redirect to dashboard after brief success message
        setTimeout(() => {
          navigate('/dashboard')
        }, 2000)

      } catch (err) {
        console.error('Auth success processing error:', err)
        setError(err instanceof Error ? err.message : 'Authentication processing failed')
        setStatus('error')
      }
    }

    handleAuthSuccess()
  }, [searchParams, navigate])

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <Card className="w-full max-w-md">
            <Card.Header>
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center">
                  <Spinner size="large" />
                </div>
                <Title as="h3">Procesando Autenticación</Title>
                <Text>
                  Configurando tu sesión de usuario...
                </Text>
              </div>
            </Card.Header>
          </Card>
        )

      case 'success':
        return (
          <Card className="w-full max-w-md">
            <Card.Header>
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                  <CheckCircleIcon size={32} className="text-green-600" />
                </div>
                <Title as="h3" className="text-green-900">¡Bienvenido!</Title>
                {userInfo && (
                  <Text>
                    Conectado a: <strong>{userInfo.store_name}</strong>
                    <br />
                    <Text as="span" fontSize="caption">
                      Tienda {userInfo.primary_store_id} • {userInfo.email}
                    </Text>
                    <br />
                    <Text as="span" fontSize="caption">Redirigiendo al dashboard...</Text>
                  </Text>
                )}
              </div>
            </Card.Header>
          </Card>
        )

      case 'error':
        return (
          <Card className="w-full max-w-md">
            <Card.Header>
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                  <ExclamationCircleIcon size={32} className="text-red-600" />
                </div>
                <Title as="h3" className="text-red-900">Error de Autenticación</Title>
                <Text className="text-red-700">
                  {error || 'No se pudo completar la autenticación'}
                </Text>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="space-y-3 text-center">
                <Button
                  onClick={() => navigate('/')}
                  fullWidth
                >
                  Volver al Login
                </Button>
                <Button
                  appearance="neutral"
                  onClick={() => window.location.reload()}
                  fullWidth
                >
                  Intentar Nuevamente
                </Button>
              </div>
            </Card.Body>
          </Card>
        )
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--nimbus-colors-primary-surface)] to-white">
      <div className="flex min-h-screen items-center justify-center p-4">
        {renderContent()}
      </div>
    </div>
  )
}